import React, { Component } from 'react';
import { func, number, object, string } from 'prop-types';
import classNames from 'classnames';

import { intlShape } from '../../../util/reactIntl';
import {
  getStartHours,
  getEndHours,
  isInRange,
  isSameDate,
  isDayMomentInsideRange,
  timeOfDayFromLocalToTimeZone,
  timeOfDayFromTimeZoneToLocal,
  isDateSameOrAfter,
  findNextBoundary,
  timestampToDate,
  formatDateIntoPartials,
  monthIdString,
  getStartOf,
  initialVisibleMonth,
} from '../../../util/dates';
import { propTypes } from '../../../util/types';
import { bookingDateRequired } from '../../../util/validators';
import { FieldDateInput, FieldSelect, IconArrowHead } from '../../../components';

import css from './FieldDateAndTimeInput.module.css';

// dayCountAvailableForBooking is the maximum number of days forwards during which a booking can be made.
// This is limited due to Stripe holding funds up to 90 days from the
// moment they are charged:
// https://stripe.com/docs/connect/account-balances#holding-funds
//
// See also the API reference for querying time slots:
// https://www.sharetribe.com/api-reference/marketplace.html#query-time-slots

const TODAY = new Date();

const nextMonthFn = (currentMoment, timeZone) =>
  getStartOf(currentMoment, 'month', timeZone, 1, 'months');
const prevMonthFn = (currentMoment, timeZone) =>
  getStartOf(currentMoment, 'month', timeZone, -1, 'months');

const endOfRange = (date, dayCountAvailableForBooking, timeZone) => {
  return getStartOf(date, 'day', timeZone, dayCountAvailableForBooking - 1, 'days');
};

const getAvailableStartTimes = (intl, timeZone, bookingStart, timeSlotsOnSelectedDate) => {
  if (timeSlotsOnSelectedDate.length === 0 || !timeSlotsOnSelectedDate[0] || !bookingStart) {
    return [];
  }
  const bookingStartDate = getStartOf(bookingStart, 'day', timeZone);

  const allHours = timeSlotsOnSelectedDate.reduce((availableHours, t) => {
    const startDate = t.attributes.start;
    const endDate = t.attributes.end;
    const nextDate = getStartOf(bookingStartDate, 'day', timeZone, 1, 'days');

    // If the start date is after timeslot start, use the start date.
    // Otherwise use the timeslot start time.
    const startLimit = isDateSameOrAfter(bookingStartDate, startDate)
      ? bookingStartDate
      : startDate;

    // If date next to selected start date is inside timeslot use the next date to get the hours of full day.
    // Otherwise use the end of the timeslot.
    const endLimit = isDateSameOrAfter(endDate, nextDate) ? nextDate : endDate;

    const hours = getStartHours(startLimit, endLimit, timeZone, intl);
    return availableHours.concat(hours);
  }, []);
  return allHours;
};

const getAvailableEndTimes = (
  intl,
  timeZone,
  bookingStartTime,
  bookingEndDate,
  selectedTimeSlot
) => {
  if (!selectedTimeSlot || !selectedTimeSlot.attributes || !bookingEndDate || !bookingStartTime) {
    return [];
  }

  const endDate = selectedTimeSlot.attributes.end;
  const bookingStartTimeAsDate = timestampToDate(bookingStartTime);

  const dayAfterBookingEnd = getStartOf(bookingEndDate, 'day', timeZone, 1, 'days');
  const dayAfterBookingStart = getStartOf(bookingStartTimeAsDate, 'day', timeZone, 1, 'days');
  const startOfEndDay = getStartOf(bookingEndDate, 'day', timeZone);

  let startLimit;
  let endLimit;

  if (!isDateSameOrAfter(startOfEndDay, bookingStartTimeAsDate)) {
    startLimit = bookingStartTimeAsDate;
    endLimit = isDateSameOrAfter(dayAfterBookingStart, endDate) ? endDate : dayAfterBookingStart;
  } else {
    // If the end date is on the same day as the selected booking start time
    // use the start time as limit. Otherwise use the start of the selected end date.
    startLimit = isDateSameOrAfter(bookingStartTimeAsDate, startOfEndDay)
      ? bookingStartTimeAsDate
      : startOfEndDay;

    // If the selected end date is on the same day as timeslot end, use the timeslot end.
    // Else use the start of the next day after selected date.
    endLimit = isSameDate(getStartOf(endDate, 'day', timeZone), startOfEndDay)
      ? endDate
      : dayAfterBookingEnd;
  }

  return getEndHours(startLimit, endLimit, timeZone, intl);
};

const getTimeSlots = (timeSlots, date, timeZone) => {
  return timeSlots && timeSlots[0]
    ? timeSlots.filter(t => {
        return isInRange(date, t.attributes.start, t.attributes.end, 'day', timeZone);
      })
    : [];
};

// Use start date to calculate the first possible start time or times, end date and end time or times.
// If the selected value is passed to function it will be used instead of calculated value.
const getAllTimeValues = (
  intl,
  timeZone,
  timeSlots,
  startDate,
  selectedStartTime,
  selectedEndDate,
  minimumBookingHours
) => {
  const startTimes = selectedStartTime
    ? []
    : getAvailableStartTimes(
        intl,
        timeZone,
        startDate,
        getTimeSlots(timeSlots, startDate, timeZone)
      );

  // Value selectedStartTime is a string when user has selected it through the form.
  // That's why we need to convert also the timestamp we use as a default
  // value to string for consistency. This is expected later when we
  // want to compare the sartTime and endTime.
  const startTime = selectedStartTime
    ? selectedStartTime
    : startTimes.length > 0 && startTimes[0] && startTimes[0].timestamp
    ? startTimes[0].timestamp.toString()
    : null;

  const startTimeAsDate = startTime ? timestampToDate(startTime) : null;

  // Note: We need to remove 1ms from the calculated endDate so that if the end
  // date would be the next day at 00:00 the day in the form is still correct.
  // Because we are only using the date and not the exact time we can remove the
  // 1ms.
  const endDate = selectedEndDate
    ? selectedEndDate
    : startTimeAsDate
    ? new Date(findNextBoundary(startTimeAsDate, 'hour', timeZone).getTime() - 1)
    : null;

  const selectedTimeSlot = timeSlots.find(t =>
    isInRange(startTimeAsDate, t.attributes.start, t.attributes.end)
  );

  const endTimes = getAvailableEndTimes(intl, timeZone, startTime, endDate, selectedTimeSlot);
  const defaultEndOption = pickDefaultEndTimeOption(endTimes, startTime, minimumBookingHours);
  const endTime = defaultEndOption?.timestamp ? defaultEndOption.timestamp.toString() : null;

  return { startTime, endDate, endTime, selectedTimeSlot };
};

const getMonthlyTimeSlots = (monthlyTimeSlots, date, timeZone) => {
  const monthId = monthIdString(date, timeZone);

  return !monthlyTimeSlots || Object.keys(monthlyTimeSlots).length === 0
    ? []
    : monthlyTimeSlots[monthId] && monthlyTimeSlots[monthId].timeSlots
    ? monthlyTimeSlots[monthId].timeSlots
    : [];
};

const normalizeHm = hm => {
  if (!hm) {
    return null;
  }
  const parts = String(hm)
    .trim()
    .split(':');
  if (parts.length !== 2) {
    return String(hm).trim();
  }
  const h = Number.parseInt(parts[0], 10);
  const m = Number.parseInt(parts[1], 10);
  if (!Number.isFinite(h) || !Number.isFinite(m)) {
    return String(hm).trim();
  }
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

// End-of-day midnight is localized as "0:00" / "00:00"; show "24:00" in the end selector.
const endTimeLabel = timeOfDay => {
  const normalized = normalizeHm(timeOfDay);
  return normalized === '00:00' || normalized === '24:00' ? '24:00' : timeOfDay;
};

const hmToMinutes = hm => {
  const normalized = normalizeHm(hm);
  if (!normalized) {
    return null;
  }
  const [h, m] = normalized.split(':').map(v => Number.parseInt(v, 10));
  if (!Number.isFinite(h) || !Number.isFinite(m)) {
    return null;
  }
  return h * 60 + m;
};

const timeOfDayFromTimestamp = (timestamp, intl, timeZone) => {
  if (!timestamp) {
    return null;
  }
  const date = timestampToDate(timestamp);
  return formatDateIntoPartials(date, intl, { timeZone })?.time || null;
};

// Keep previous hour if possible. If not available, pick the next available option.
const pickSameOrNextTimeOption = (options, wantedHm) => {
  if (!Array.isArray(options) || options.length === 0) {
    return null;
  }
  const wanted = normalizeHm(wantedHm);
  if (!wanted) {
    return options[0];
  }

  const exact = options.find(o => normalizeHm(o?.timeOfDay) === wanted);
  if (exact) {
    return exact;
  }

  const wantedMinutes = hmToMinutes(wanted);
  if (wantedMinutes == null) {
    return options[0];
  }

  const next = options.find(o => {
    const curMinutes = hmToMinutes(o?.timeOfDay);
    return curMinutes != null && curMinutes >= wantedMinutes;
  });

  return next || options[0];
};

const DEFAULT_BOOKING_DURATION_HOURS = 6;

const resolveDefaultBookingDurationHours = minimumBookingHours => {
  const parsed = parseInt(minimumBookingHours, 10);
  return parsed > 0 ? parsed : DEFAULT_BOOKING_DURATION_HOURS;
};

// Pick the first end-time option at least minimumBookingHours after start; fall back to last option.
const pickDefaultEndTimeOption = (endTimes, startTime, minimumBookingHours) => {
  if (!Array.isArray(endTimes) || endTimes.length === 0 || !startTime) {
    return null;
  }
  const durationHours = resolveDefaultBookingDurationHours(minimumBookingHours);
  const minEndMs = Number(startTime) + durationHours * 60 * 60 * 1000;
  const match = endTimes.find(o => o?.timestamp && o.timestamp >= minEndMs);
  return match || endTimes[endTimes.length - 1];
};

// IconArrowHead component might not be defined if exposed directly to the file.
// This component is called before IconArrowHead component in components/index.js
const PrevIcon = props => (
  <IconArrowHead {...props} direction="left" rootClassName={css.arrowIcon} />
);
const NextIcon = props => (
  <IconArrowHead {...props} direction="right" rootClassName={css.arrowIcon} />
);

const Next = props => {
  const { currentMonth, dayCountAvailableForBooking, timeZone } = props;
  const nextMonthDate = nextMonthFn(currentMonth, timeZone);

  return isDateSameOrAfter(
    nextMonthDate,
    endOfRange(TODAY, dayCountAvailableForBooking, timeZone)
  ) ? null : (
    <NextIcon />
  );
};
const Prev = props => {
  const { currentMonth, timeZone } = props;
  const prevMonthDate = prevMonthFn(currentMonth, timeZone);
  const currentMonthDate = getStartOf(TODAY, 'month', timeZone);

  return isDateSameOrAfter(prevMonthDate, currentMonthDate) ? <PrevIcon /> : null;
};

/////////////////////////////////////
// FieldDateAndTimeInput component //
/////////////////////////////////////
class FieldDateAndTimeInput extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentMonth: getStartOf(TODAY, 'month', props.timeZone),
    };

    this.fetchMonthData = this.fetchMonthData.bind(this);
    this.onMonthClick = this.onMonthClick.bind(this);
    this.onBookingStartDateChange = this.onBookingStartDateChange.bind(this);
    this.onBookingStartTimeChange = this.onBookingStartTimeChange.bind(this);
    this.onBookingEndDateChange = this.onBookingEndDateChange.bind(this);
    this.isOutsideRange = this.isOutsideRange.bind(this);
  }

  fetchMonthData(date) {
    const { listingId, timeZone, onFetchTimeSlots, dayCountAvailableForBooking } = this.props;
    const endOfRangeDate = endOfRange(TODAY, dayCountAvailableForBooking, timeZone);

    // Don't fetch timeSlots for past months or too far in the future
    if (isInRange(date, TODAY, endOfRangeDate)) {
      // Use "today", if the first day of given month is in the past
      const start = isDateSameOrAfter(TODAY, date) ? TODAY : date;

      // Use endOfRangeDate, if the first day of the next month is too far in the future
      const nextMonthDate = nextMonthFn(date, timeZone);
      const end = isDateSameOrAfter(nextMonthDate, endOfRangeDate)
        ? getStartOf(endOfRangeDate, 'day', timeZone)
        : nextMonthDate;

      // Fetch time slots for given time range
      onFetchTimeSlots(listingId, start, end, timeZone);
    }
  }

  componentDidUpdate(prevProps) {
    const prevDate = prevProps.values?.bookingStartDate?.date;
    const currDate = this.props.values?.bookingStartDate?.date;
    if (currDate && currDate !== prevDate) {
      const { timeZone } = this.props;
      const newMonth = getStartOf(currDate, 'month', timeZone);
      const newMonthId = monthIdString(newMonth, timeZone);
      const curMonthId = monthIdString(this.state.currentMonth, timeZone);
      if (newMonthId !== curMonthId) {
        this.setState({ currentMonth: newMonth });
      }
    }
  }

  onMonthClick(monthFn) {
    const { onMonthChanged, timeZone } = this.props;

    this.setState(
      prevState => ({ currentMonth: monthFn(prevState.currentMonth, timeZone) }),
      () => {
        // Callback function after month has been updated.
        // react-dates component has next and previous months ready (but inivisible).
        // we try to populate those invisible months before user advances there.
        this.fetchMonthData(monthFn(this.state.currentMonth, timeZone));

        // If previous fetch for month data failed, try again.
        const monthId = monthIdString(this.state.currentMonth, timeZone);
        const currentMonthData = this.props.monthlyTimeSlots[monthId];
        if (currentMonthData && currentMonthData.fetchTimeSlotsError) {
          this.fetchMonthData(this.state.currentMonth);
        }

        // Call onMonthChanged function if it has been passed in among props.
        if (onMonthChanged) {
          onMonthChanged(monthId);
        }
      }
    );
  }

  onBookingStartDateChange = value => {
    const { monthlyTimeSlots, timeZone, intl, form, values, minimumBookingHours } = this.props;
    if (!value || !value.date) {
      form.batch(() => {
        form.change('bookingStartTime', null);
        form.change('bookingEndDate', { date: null });
        form.change('bookingEndTime', null);
      });
      // Reset the currentMonth too if bookingStartDate is cleared
      this.setState({ currentMonth: getStartOf(TODAY, 'month', timeZone) });

      return;
    }

    // This callback function (onBookingStartDateChange) is called from react-dates component.
    // It gets raw value as a param - browser's local time instead of time in listing's timezone.
    const startDate = timeOfDayFromLocalToTimeZone(value.date, timeZone);
    // Ensure month data is fetched for the selected start date.
    this.fetchMonthData(getStartOf(startDate, 'month', timeZone));
    // Use the selected date's month (not state.currentMonth) so that time slots from the
    // correct month are used when the calendar is showing a month other than currentMonth.
    const effectiveMonth = getStartOf(startDate, 'month', timeZone);
    const timeSlots = getMonthlyTimeSlots(monthlyTimeSlots, effectiveMonth, timeZone);
    const timeSlotsOnSelectedDate = getTimeSlots(timeSlots, startDate, timeZone);
    const availableStartTimes = getAvailableStartTimes(
      intl,
      timeZone,
      startDate,
      timeSlotsOnSelectedDate
    );

    const previousStartHm = timeOfDayFromTimestamp(values?.bookingStartTime, intl, timeZone);
    const pickedStartOption = pickSameOrNextTimeOption(availableStartTimes, previousStartHm);
    const pickedStartTime = pickedStartOption ? String(pickedStartOption.timestamp) : null;

    const { startTime, endDate, selectedTimeSlot } = getAllTimeValues(
      intl,
      timeZone,
      timeSlotsOnSelectedDate,
      startDate,
      pickedStartTime,
      null,
      minimumBookingHours
    );

    const resolvedStartTime = pickedStartTime || startTime;
    const availableEndTimes = getAvailableEndTimes(
      intl,
      timeZone,
      resolvedStartTime,
      endDate,
      selectedTimeSlot
    );
    const previousEndHm = timeOfDayFromTimestamp(values?.bookingEndTime, intl, timeZone);
    const pickedEndOption = previousEndHm
      ? pickSameOrNextTimeOption(availableEndTimes, previousEndHm)
      : pickDefaultEndTimeOption(availableEndTimes, resolvedStartTime, minimumBookingHours);
    const resolvedEndTime = pickedEndOption ? String(pickedEndOption.timestamp) : null;

    form.batch(() => {
      form.change('bookingStartTime', resolvedStartTime);
      form.change('bookingEndDate', { date: endDate });
      form.change('bookingEndTime', resolvedEndTime);
    });
  };

  onBookingStartTimeChange = value => {
    const { monthlyTimeSlots, timeZone, intl, form, values, minimumBookingHours } = this.props;
    const startDate = values?.bookingStartDate?.date;
    if (!startDate) {
      return;
    }
    const effectiveMonth = getStartOf(startDate, 'month', timeZone);
    const timeSlots = getMonthlyTimeSlots(monthlyTimeSlots, effectiveMonth, timeZone);
    const timeSlotsOnSelectedDate = getTimeSlots(timeSlots, startDate, timeZone);

    const { endDate, endTime } = getAllTimeValues(
      intl,
      timeZone,
      timeSlotsOnSelectedDate,
      startDate,
      value,
      null,
      minimumBookingHours
    );

    form.batch(() => {
      form.change('bookingEndDate', { date: endDate });
      form.change('bookingEndTime', endTime);
    });
  };

  onBookingEndDateChange = value => {
    const { monthlyTimeSlots, timeZone, intl, form, values, minimumBookingHours } = this.props;
    if (!value || !value.date) {
      form.change('bookingEndTime', null);
      return;
    }

    // This callback function (onBookingStartDateChange) is called from react-dates component.
    // It gets raw value as a param - browser's local time instead of time in listing's timezone.
    const endDate = timeOfDayFromLocalToTimeZone(value.date, timeZone);

    const bookingStartDate = values?.bookingStartDate;
    const bookingStartTime = values?.bookingStartTime;
    const startDate = bookingStartDate?.date;
    if (!startDate || !bookingStartTime) {
      form.change('bookingEndTime', null);
      return;
    }
    const effectiveMonth = getStartOf(startDate, 'month', timeZone);
    const timeSlots = getMonthlyTimeSlots(monthlyTimeSlots, effectiveMonth, timeZone);
    const timeSlotsOnSelectedDate = getTimeSlots(timeSlots, startDate, timeZone);

    const { endTime } = getAllTimeValues(
      intl,
      timeZone,
      timeSlotsOnSelectedDate,
      startDate,
      bookingStartTime,
      endDate,
      minimumBookingHours
    );

    form.change('bookingEndTime', endTime);
  };

  isOutsideRange(day, bookingStartDate, selectedTimeSlot, timeZone) {
    if (!selectedTimeSlot) {
      return true;
    }

    // 'day' is pointing to browser's local time-zone (react-dates gives these).
    // However, bookingStartDate and selectedTimeSlot refer to times in listing's timeZone.
    const localizedDay = timeOfDayFromLocalToTimeZone(day, timeZone);
    // Given day (endDate) should be after the start of the day of selected booking start date.
    const startDate = getStartOf(bookingStartDate, 'day', timeZone);
    // 00:00 would return wrong day as the end date.
    // Removing 1 millisecond, solves the exclusivity issue.
    const inclusiveEnd = new Date(selectedTimeSlot.attributes.end.getTime() - 1);
    // Given day (endDate) should be before the "next" day of selected timeSlots end.
    const endDate = getStartOf(inclusiveEnd, 'day', timeZone, 1, 'days');
    return !(
      isDateSameOrAfter(localizedDay, startDate) && isDateSameOrAfter(endDate, localizedDay)
    );
  }

  render() {
    const {
      rootClassName,
      className,
      formId,
      startDateInputProps,
      // endDateInputProps,
      values,
      monthlyTimeSlots,
      timeZone,
      intl,
      dayCountAvailableForBooking,
      disabledUntilNoGuest,
      minimumBookingHours,
    } = this.props;

    const classes = classNames(rootClassName || css.root, className);
    const safeValues = values || {};

    const bookingStartDate =
      safeValues.bookingStartDate && safeValues.bookingStartDate.date
        ? safeValues.bookingStartDate.date
        : null;
    const bookingStartTime = safeValues.bookingStartTime ? safeValues.bookingStartTime : null;
    const bookingEndDate =
      safeValues.bookingEndDate && safeValues.bookingEndDate.date
        ? safeValues.bookingEndDate.date
        : null;

    // Usar el mes de la fecha elegida (p. ej. precarga desde /s/) para resolver slots, no solo el mes del calendario.
    const effectiveMonthForSlots = bookingStartDate
      ? getStartOf(bookingStartDate, 'month', timeZone)
      : this.state.currentMonth;
    const timeSlotsOnSelectedMonth = getMonthlyTimeSlots(
      monthlyTimeSlots,
      effectiveMonthForSlots,
      timeZone
    );
    const timeSlotsOnSelectedDate = getTimeSlots(
      timeSlotsOnSelectedMonth,
      bookingStartDate,
      timeZone
    );

    const availableStartTimes = getAvailableStartTimes(
      intl,
      timeZone,
      bookingStartDate,
      timeSlotsOnSelectedDate
    );

    const firstAvailableStartTime =
      availableStartTimes.length > 0 && availableStartTimes[0] && availableStartTimes[0].timestamp
        ? String(availableStartTimes[0].timestamp)
        : null;

    const { startTime, endDate, selectedTimeSlot } = getAllTimeValues(
      intl,
      timeZone,
      timeSlotsOnSelectedDate,
      bookingStartDate,
      bookingStartTime || firstAvailableStartTime,
      bookingEndDate || bookingStartDate,
      minimumBookingHours
    );

    const availableEndTimes = getAvailableEndTimes(
      intl,
      timeZone,
      bookingStartTime || startTime,
      bookingEndDate || endDate,
      selectedTimeSlot
    );

    // Resolver el bloqueo por el mes del propio día renderizado (no por el mes de la fecha
    // seleccionada). Así, al navegar a meses anteriores/posteriores el calendario usa los slots
    // de ese mes en lugar de quedar clavado al mes de bookingStartDate. La clave 'YYYY-MM' del día
    // coincide con las claves de monthlyTimeSlots (ver monthIdString en el duck).
    const isDayBlocked = day => {
      const slotsForDayMonth = monthlyTimeSlots?.[day.format('YYYY-MM')]?.timeSlots || [];
      return !slotsForDayMonth.find(timeSlot =>
        isDayMomentInsideRange(day, timeSlot.attributes.start, timeSlot.attributes.end, timeZone)
      );
    };

    const nextBoundary = findNextBoundary(TODAY, 'hour', timeZone);
    let placeholderTime = '08:00';
    try {
      placeholderTime = formatDateIntoPartials(nextBoundary, intl, { timeZone })?.time;
    } catch (error) {
      // No need to handle error
    }

    const startOfToday = getStartOf(TODAY, 'day', timeZone);
    const bookingEndTimeAvailable = bookingStartDate && (bookingStartTime || startTime);
    return (
      <div className={classes}>
        <div className={css.formRow}>
          <div className={classNames(css.field, css.startDate)}>
            <FieldDateInput
              className={css.fieldDateInput}
              name="bookingStartDate"
              id={formId ? `${formId}.bookingStartDate` : 'bookingStartDate'}
              label={startDateInputProps.label}
              placeholderText={startDateInputProps.placeholderText}
              format={v =>
                v && v.date ? { date: timeOfDayFromTimeZoneToLocal(v.date, timeZone) } : v
              }
              parse={v =>
                v && v.date ? { date: timeOfDayFromLocalToTimeZone(v.date, timeZone) } : v
              }
              initialVisibleMonth={initialVisibleMonth(bookingStartDate || startOfToday, timeZone)}
              isDayBlocked={isDayBlocked}
              onChange={this.onBookingStartDateChange}
              onPrevMonthClick={() => this.onMonthClick(prevMonthFn)}
              onNextMonthClick={() => this.onMonthClick(nextMonthFn)}
              navNext={
                <Next
                  currentMonth={this.state.currentMonth}
                  timeZone={timeZone}
                  dayCountAvailableForBooking={dayCountAvailableForBooking}
                />
              }
              navPrev={<Prev currentMonth={this.state.currentMonth} timeZone={timeZone} />}
              useMobileMargins
              validate={bookingDateRequired(
                intl.formatMessage({ id: 'BookingTimeForm.requiredDate' })
              )}
              onClose={event =>
                this.setState({
                  currentMonth: getStartOf(event?.date ?? TODAY, 'month', this.props.timeZone),
                })
              }
              disabled={disabledUntilNoGuest}
            />
          </div>
        </div>
        <div className={css.formRow}>
          <div className={css.field}>
            <FieldSelect
              name="bookingStartTime"
              id={formId ? `${formId}.bookingStartTime` : 'bookingStartTime'}
              className={bookingStartDate ? css.fieldSelect : css.fieldSelectDisabled}
              selectClassName={bookingStartDate ? css.select : css.selectDisabled}
              label={intl.formatMessage({ id: 'FieldDateAndTimeInput.startTime' })}
              disabled={!bookingStartDate}
              onChange={this.onBookingStartTimeChange}
            >
              {bookingStartDate ? (
                availableStartTimes.map(p => (
                  <option key={String(p.timestamp)} value={String(p.timestamp)}>
                    {p.timeOfDay}
                  </option>
                ))
              ) : (
                <option>{placeholderTime}</option>
              )}
            </FieldSelect>
          </div>

          <div className={bookingStartDate ? css.lineBetween : css.lineBetweenDisabled}>-</div>

          <div className={css.field}>
            <FieldSelect
              name="bookingEndTime"
              id={formId ? `${formId}.bookingEndTime` : 'bookingEndTime'}
              className={bookingStartDate ? css.fieldSelect : css.fieldSelectDisabled}
              selectClassName={bookingStartDate ? css.select : css.selectDisabled}
              label={intl.formatMessage({ id: 'FieldDateAndTimeInput.endTime' })}
              disabled={!bookingEndTimeAvailable}
            >
              {bookingEndTimeAvailable ? (
                availableEndTimes.map(p => (
                  <option key={String(p.timestamp)} value={String(p.timestamp)}>
                    {endTimeLabel(p.timeOfDay)}
                  </option>
                ))
              ) : (
                <option>{placeholderTime}</option>
              )}
            </FieldSelect>
          </div>
        </div>
      </div>
    );
  }
}

FieldDateAndTimeInput.defaultProps = {
  rootClassName: null,
  className: null,
  startDateInputProps: null,
  endDateInputProps: null,
  startTimeInputProps: null,
  endTimeInputProps: null,
  listingId: null,
  monthlyTimeSlots: null,
  timeZone: null,
  minimumBookingHours: null,
};

FieldDateAndTimeInput.propTypes = {
  rootClassName: string,
  className: string,
  formId: string,
  startDateInputProps: object,
  endDateInputProps: object,
  startTimeInputProps: object,
  endTimeInputProps: object,
  form: object.isRequired,
  values: object.isRequired,
  listingId: propTypes.uuid,
  monthlyTimeSlots: object,
  onFetchTimeSlots: func.isRequired,
  timeZone: string,
  dayCountAvailableForBooking: number,
  minimumBookingHours: number,

  // from injectIntl
  intl: intlShape.isRequired,
};

export {
  getMonthlyTimeSlots,
  getTimeSlots,
  getAvailableStartTimes,
  getAvailableEndTimes,
  getAllTimeValues,
  pickDefaultEndTimeOption,
  resolveDefaultBookingDurationHours,
  DEFAULT_BOOKING_DURATION_HOURS,
};

export default FieldDateAndTimeInput;
